import React, { useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import { Box, Button, IconButton, Tooltip, Chip, Typography } from '@mui/material';
import { Delete, Edit, Add, HowToReg, RemoveCircleOutline, Verified, Print } from '@mui/icons-material';

import CustomDialog from '../../../layout/CustomDialog';
import HomologationRegistroDialog from './HomologationRegistroDialog';
import HomologationAssinaturaDialog from './HomologationAssinaturaDialog';

import { retornaDataBr, retornaDataHoraBr } from '../../../../utils/retornaDataFormatada';

import { podeCancelarItem, podeEditarItem, podeImprimirItem } from '../../../../hooks/usePermisionMRT';

import * as HomologationAPI from '../../../../api/homologation';
import { printPdf } from '../../../../utils/funcoes';

function RegistrosTabPanel({ module, empresaTabelaId }) {
    const { state, actions, context, dialogs, mrt, options } = module;

    const columns = useMemo(
        () => [
            {
                id: 'assinatura',
                header: 'Assinatura',
                Cell: ({ row }) => {
                    const assinatura = row.original?.AssinaturaAtiva;

                    if (!assinatura) {
                        return <Chip label="Pendente" color="warning" size="small" variant="outlined" />;
                    }

                    const nome = assinatura?.Pessoa?.descricao || 'Usuário';
                    const data = assinatura?.createdAt ? retornaDataBr(assinatura.createdAt, 'date') : '-';

                    return (
                        <Tooltip title={`Assinado por ${nome} em ${data}`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Verified color="success" sx={{ fontSize: 18 }} />
                                <Chip label="Assinado" color="success" size="small" />
                            </Box>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'CriadoPor.descricao',
                header: 'Autor',
                columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
            },
            {
                accessorKey: 'descricao',
                header: 'Descrição',
                muiTableBodyCellProps: ({ row }) => ({
                    onClick: () => {
                        console.info(row.original);
                    },
                }),
            },
            {
                accessorKey: 'Situacao.descricao',
                header: 'Situação',
                columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
                filterVariant: 'select',
                filterSelectOptions: options.optionsSituacaoFilter || [],
            },
            {
                accessorKey: 'data',
                header: 'Data',
                Cell: ({ cell }) => (cell.getValue() ? retornaDataBr(cell.getValue(), 'date') : ''),
            },
            {
                accessorKey: 'prazo',
                header: 'Prazo',
                Cell: ({ cell }) => (cell.getValue() ? retornaDataBr(cell.getValue(), 'date') : ''),
            },
            {
                accessorKey: 'observacao',
                header: 'Observação',
            },
        ],
        [options.optionsSituacaoFilter]
    );

    return (
        <div>
            {dialogs.openDeleteDialog && (
                <CustomDialog
                    title="Delete REGISTRO!"
                    content="Você tem certeza?"
                    onClose={actions.handleCancelar}
                    onConfirm={actions.handleDeletar}
                />
            )}

            {dialogs.openDialog && (
                <HomologationRegistroDialog
                    open={dialogs.openDialog}
                    onClose={actions.handleCancelar}
                    onSave={actions.handleSalvarDraft}
                    formData={dialogs.draft}
                    itens={state.itens.map((item) => ({
                        id: item.id,
                        descricao: item?.descricao || item?.menuSub?.descricao || `Item ${item.id}`,
                    }))}
                    empresaTabelaId={empresaTabelaId}
                    isSaving={state.isSaving}
                />
            )}

            {dialogs.openAssinarDialog && (
                <HomologationAssinaturaDialog
                    open={dialogs.openAssinarDialog}
                    onClose={actions.handleCloseAssinarDialog}
                    onConfirm={actions.handleAssinarRegistro}
                    title="Assinar Registro"
                    confirmLabel="Assinar"
                    loading={dialogs.isAssinando}
                />
            )}

            {dialogs.openRemoverAssinaturaDialog && (
                <HomologationAssinaturaDialog
                    open={dialogs.openRemoverAssinaturaDialog}
                    onClose={actions.handleCloseRemoverAssinaturaDialog}
                    onConfirm={actions.handleRemoverAssinaturaRegistro}
                    title="Remover Assinatura do Registro"
                    confirmLabel="Remover assinatura"
                    loading={dialogs.isAssinando}
                />
            )}

            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                <label>Item:</label>
                <select
                    value={state.itemSelecionado || ''}
                    onChange={actions.handleChangeItemSelecionado}
                    style={{ minWidth: 320, padding: 8 }}
                >
                    <option value="">Selecione um item</option>
                    {state.itens.map((item) => (
                        <option key={item.id} value={item.id}>
                            {item?.descricao || item?.menuSub?.descricao || `Item ${item.id}`}
                        </option>
                    ))}
                </select>
            </Box>

            <MaterialReactTable
                columns={columns}
                data={state.rows}
                enableRowSelection
                enableRowActions
                getRowId={(row) => row.uuid}
                manualFiltering
                manualPagination
                manualSorting
                enableColumnFilterModes
                enableGlobalFilterModes
                enableColumnOrdering
                localization={MRT_Localization_PT_BR}
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        muiTableHeadCellProps: {
                            align: 'center',
                        },
                        size: 200,
                    },
                    'mrt-row-numbers': {
                        enableColumnOrdering: true,
                        enableResizing: true,
                        muiTableHeadCellProps: {
                            sx: {
                                fontSize: '1.2rem',
                            },
                        },
                    },
                    'mrt-row-select': {
                        enableColumnActions: true,
                        enableHiding: true,
                        size: 50,
                    },
                }}
                renderTopToolbarCustomActions={() => (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                        <Tooltip title="Novo Registro">
                            <Button variant="contained" color="secondary" startIcon={<Add />} onClick={actions.handleAdicionar}>
                                Novo Registro
                            </Button>
                        </Tooltip>
                    </Box>
                )}
                renderRowActions={({ row }) => {
                    const registro = row.original;
                    const assinatura = registro?.AssinaturaAtiva;

                    const isAutor = String(registro?.createdUser) === String(context?.pessoaId);

                    const concluiRegistro = registro?.concluiRegistro === true;

                    const podeAssinar = isAutor && concluiRegistro && !assinatura;
                    const podeRemoverAssinatura = !!assinatura && isAutor;

                    const imprimirItemRegistro = async (obj) => {
                        const response = await HomologationAPI.imprimirItemRegistro(obj);
                        printPdf(response.data);
                    };

                    return (
                        <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <Tooltip title="Editar">
                                <IconButton onClick={() => actions.handleEditar(row)} disabled={!podeEditarItem()}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Deletar">
                                <IconButton
                                    color="error"
                                    disabled={!podeCancelarItem()}
                                    onClick={() => {
                                        actions.setSelectedRow(row.original);
                                        actions.setOpenDeleteDialog(true);
                                    }}
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Imprimir Registro">
                                <IconButton
                                    color="warning"
                                    onClick={() => imprimirItemRegistro(row.original)}
                                    disabled={!podeImprimirItem()}
                                >
                                    <Print />
                                </IconButton>
                            </Tooltip>

                            {podeAssinar && (
                                <Tooltip title="Assinar registro">
                                    <IconButton color="success" onClick={() => actions.handleOpenAssinarDialog(registro)}>
                                        <HowToReg />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {podeRemoverAssinatura && (
                                <Tooltip title="Remover assinatura">
                                    <IconButton
                                        color="warning"
                                        onClick={() => actions.handleOpenRemoverAssinaturaDialog(registro)}
                                    >
                                        <RemoveCircleOutline />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    );
                }}
                onColumnFiltersChange={mrt.setColumnFilters}
                onColumnFilterFnsChange={mrt.setColumnFiltersMode}
                onGlobalFilterChange={mrt.setGlobalFilter}
                onPaginationChange={mrt.setPagination}
                onSortingChange={mrt.setSorting}
                rowCount={mrt.rowCount}
                initialState={{
                    showColumnFilters: true,
                    density: 'compact',
                }}
                state={{
                    columnFilterFns: mrt.columnFilterFns,
                    columnFilters: mrt.columnFilters,
                    globalFilter: mrt.globalFilter,
                    isLoading: mrt.isLoading,
                    pagination: mrt.pagination,
                    showAlertBanner: mrt.isError,
                    showProgressBars: mrt.isRefetching,
                    sorting: mrt.sorting,
                }}
                muiToolbarAlertBannerProps={
                    mrt.isError
                        ? {
                              color: 'error',
                              children: 'Erro ao carregar registros',
                          }
                        : undefined
                }
                positionActionsColumn="first"
                positionToolbarAlertBanner="bottom"
            />
        </div>
    );
}

export default RegistrosTabPanel;
